/**
 * Web storage that is allowed to be unavailable.
 *
 * Reading `window.localStorage` *throws* a SecurityError in any context that denies
 * storage — a third-party iframe with cookies blocked, Safari Lockdown mode, "block all
 * cookies", some embedded webviews. The property access itself throws, before any
 * getItem/setItem. An `isPlatformBrowser()` check does not help: the platform *is* a
 * browser, the storage is simply refused. Safari private mode has also historically
 * returned null for `window.localStorage` outright.
 *
 * So storage must be treated as an optional cache that may vanish, never as somewhere the
 * app can rely on. LanguageService relied on it from inside an APP_INITIALIZER, so the
 * throw aborted bootstrap and those users got a blank page rather than a degraded one.
 *
 * Every helper here degrades to a no-op (or null) instead of throwing. Import these
 * instead of touching localStorage/sessionStorage directly — `npm run check:storage`
 * enforces it.
 */

type StorageKind = 'local' | 'session';

function getStore(kind: StorageKind): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    // eslint-disable-next-line no-restricted-globals -- the one sanctioned access point
    const store = kind === 'local' ? window.localStorage : window.sessionStorage;
    return store ?? null;
  } catch {
    return null;
  }
}

function read(kind: StorageKind, key: string): string | null {
  const store = getStore(kind);
  if (!store) return null;
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}

function write(kind: StorageKind, key: string, value: string): boolean {
  const store = getStore(kind);
  if (!store) return false;
  try {
    store.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function drop(kind: StorageKind, key: string): void {
  const store = getStore(kind);
  if (!store) return;
  try {
    store.removeItem(key);
  } catch {
    // Storage went away mid-session; nothing to remove.
  }
}

function listKeys(kind: StorageKind): string[] {
  const store = getStore(kind);
  if (!store) return [];
  try {
    return Object.keys(store);
  } catch {
    return [];
  }
}

function wipe(kind: StorageKind): void {
  const store = getStore(kind);
  if (!store) return;
  try {
    store.clear();
  } catch {
    // Nothing we can do; the storage is not ours to clear.
  }
}

export const safeLocalStorage = {
  isAvailable: (): boolean => getStore('local') !== null,
  getItem: (key: string): string | null => read('local', key),
  /** Returns false when the value could not be stored (denied, or quota exhausted). */
  setItem: (key: string, value: string): boolean => write('local', key, value),
  removeItem: (key: string): void => drop('local', key),
  keys: (): string[] => listKeys('local'),
  clear: (): void => wipe('local')
};

export const safeSessionStorage = {
  isAvailable: (): boolean => getStore('session') !== null,
  getItem: (key: string): string | null => read('session', key),
  setItem: (key: string, value: string): boolean => write('session', key, value),
  removeItem: (key: string): void => drop('session', key),
  keys: (): string[] => listKeys('session'),
  clear: (): void => wipe('session')
};
